import React, { useEffect, useState } from "react";
import { Box, Alert, Button, Snackbar, Typography } from "@mui/material";
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import cartOperationManager from "../../services/CartOperationManager";

interface CartErrorRecoveryProps {
  onRetry?: () => void;
}

/**
 * Component to handle cart operation errors and provide recovery options
 * Especially useful when operations fail after page refresh or network issues
 */
export const CartErrorRecovery: React.FC<CartErrorRecoveryProps> = ({
  onRetry,
}) => {
  const [failedOperations, setFailedOperations] = useState<any[]>([]);
  const [showRecoveryAlert, setShowRecoveryAlert] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  useEffect(() => {
    // Check for failed operations on mount
    const checkFailedOperations = () => {
      const failed = cartOperationManager.getFailedOperations();
      if (failed.length > 0) {
        setFailedOperations(failed);
        setShowRecoveryAlert(true);
      }
    };

    // Check immediately
    checkFailedOperations();

    // Set up periodic check for failed operations
    const interval = setInterval(checkFailedOperations, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleRetryFailedOperations = async () => {
    if (failedOperations.length === 0) return;

    setIsRecovering(true);

    try {
      // Retry each failed operation
      for (const operation of failedOperations) {
        await cartOperationManager.retryOperation(operation.id);
      }

      setFailedOperations([]);
      setShowRecoveryAlert(false);

      if (onRetry) {
        onRetry();
      }
    } catch (error) {
      console.error("Error retrying failed operations:", error);
    } finally {
      setIsRecovering(false);
    }
  };

  const handleDismissAlert = () => {
    setShowRecoveryAlert(false);
  };

  const handleClearFailedOperations = () => {
    // Clear failed operations from manager
    failedOperations.forEach((operation) => {
      cartOperationManager.removeOperation(operation.id);
    });

    setFailedOperations([]);
    setShowRecoveryAlert(false);
  };

  if (!showRecoveryAlert || failedOperations.length === 0) {
    return null;
  }

  return (
    <Snackbar
      open={showRecoveryAlert}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{ zIndex: 1400 }}
    >
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        sx={{
          minWidth: "400px",
          "& .MuiAlert-message": {
            display: "flex",
            flexDirection: "column",
            gap: 1,
          },
        }}
        action={
          <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleRetryFailedOperations}
              disabled={isRecovering}
              startIcon={<RefreshIcon />}
              sx={{ fontSize: "0.75rem" }}
            >
              {isRecovering ? "Reintentando..." : "Reintentar"}
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={handleClearFailedOperations}
              sx={{ fontSize: "0.75rem" }}
            >
              Descartar
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={handleDismissAlert}
              sx={{ fontSize: "0.75rem" }}
            >
              Cerrar
            </Button>
          </Box>
        }
      >
        <Typography variant="body2" fontWeight="medium">
          Cambios del carrito no guardados
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {failedOperations.length} operación
          {failedOperations.length > 1 ? "es" : ""} del carrito
          {failedOperations.length > 1 ? " fallaron" : " falló"} y pueden
          haberse perdido.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Esto puede ocurrir después de recargar la página o problemas de
          conexión.
        </Typography>
      </Alert>
    </Snackbar>
  );
};

export default CartErrorRecovery;
